import moment from 'moment';
import { ApiResult, apiUrl } from '../../../api';
import {
  Contact,
  ContactDuplicate,
  ContactTag,
  CreateExternalContactBundle,
  ExternalContact,
} from '../../../models/Contact';
import { InternalOffice } from '../../../models/Office';
import {
  ExternalProjectContact,
  ImportProjectMembers,
  InternalProjectContact,
  NewInternalProjectMemberRequest,
} from '../../../models/ProjectContacts';

import {
  CompanyId,
  ContactId,
  ContactType,
  DateTimeString,
  OfficeId,
  ProjectId,
} from '../../../models/Types';
import {
  ContactsCompaniesSearchResult,
  ProjectMemberSearchResult,
} from '../../../models/search';
import { getAccessToken } from '../../../store/authEffect';
import fetchWithRetry from '../../../util/fetchWithRetry';

export const apiCreateExternalContactBundle: (
  createRequest: CreateExternalContactBundle
) => Promise<ApiResult<ExternalContact>> = async (createRequest) => {
  const result = await fetchWithRetry(
    `${apiUrl}/contact/Contact/ExternalContactBundle`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ ...createRequest }),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiSearchContacts: (
  searchString: string,
  contactType?: ContactType
) => Promise<ApiResult<Contact[]>> = async (searchString, contactType) => {
  const convertedType = contactType
    ? contactType === 'InternalContact'
      ? 'Internal'
      : 'External'
    : undefined;
  const contactTypeSearchString = convertedType
    ? `InternalExternal eq '${convertedType}'`
    : '';
  const result = await fetchWithRetry(
    `${apiUrl}/search/Search?searchType=companyContacts&searchString=${contactTypeSearchString}${
      contactTypeSearchString ? '%26' : ''
    }${encodeURI(
      `QuickSearch like '${searchString}'`
    )}%26${encodeURI(
      `CompanyContact eq 'Contact'`
    )}&forceResult=true`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  let data: Contact[] = null;

  if (result.status >= 200 && result.status < 300) {
    data = ((await result.json()) as ContactsCompaniesSearchResult).items
      .map(({ data }) => data.contact)
      .filter((contact) => Boolean(contact));
  }

  return {
    result,
    data,
  };
};

export const apiFetchContact: (
  contactId: ContactId
) => Promise<ApiResult<Contact>> = async (contactId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/contact/Contact/${contactId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiFetchInternalOffice: (
  officeId: OfficeId
) => Promise<ApiResult<InternalOffice>> = async (officeId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/contact/InternalOffice/${officeId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiSyncContactBook: () => Promise<ApiResult<any>> = async () => {
  const result = await fetchWithRetry(`${apiUrl}/prioHealth/contactsync/me`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });

  return {
    result,
  };
};

export const apiFetchCompanyContacts: (
  companyId: CompanyId
) => Promise<ApiResult<Contact[]>> = async (companyId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/contact/Company/${companyId}/contact`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiCheckContactDuplicate: (
  values: ContactDuplicate
) => Promise<ApiResult<Contact[]>> = async (values) => {
  const keysArr = Object.keys(values);
  const valsArr = Object.values(values);
  const validationString = keysArr
    .map((key, idx) => `${key}=${valsArr[idx]}`)
    .join('&');
  const result = await fetchWithRetry(
    `${apiUrl}/contact/Contact/validation?${validationString}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiSearchContactsByProps: (
  searchString: string,
  forceThreshold: number,
  forceResult: boolean,
  signal?: AbortSignal
) => Promise<ApiResult<ContactsCompaniesSearchResult>> = async (
  searchString,
  forceThreshold,
  forceResult,
  signal
) => {
  const result = await fetchWithRetry(
    `${apiUrl}/search/Search?searchType=companyContacts&searchString=${searchString}&forceThreshold=${forceThreshold}&forceResult=${forceResult}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
      signal,
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

export const apiFetchContactTags: () => Promise<
  ApiResult<ContactTag[]>
> = async () => {
  const result = await fetchWithRetry(`${apiUrl}/contact/contactTag`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiCreateContactTag: (
  contactTagName: string
) => Promise<ApiResult<ContactTag>> = async (contactTagName) => {
  const result = await fetchWithRetry(
    `${apiUrl}/contact/ContactTag?newTagName=${contactTagName}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiGetCompanyById: (
  companyId: string
) => Promise<ApiResult<Contact>> = async (companyId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/contact/Company/${companyId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiImportProjectMember: (
  importMembers: ImportProjectMembers,
  projectId: string
) => Promise<
  ApiResult<InternalProjectContact | ExternalProjectContact>
> = async (importProjectMembers, projectId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/project/projectMember/${projectId}/import`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ ...importProjectMembers }),
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiAddInternalProjectMembers: (
  projectId: ProjectId,
  projectMembers: NewInternalProjectMemberRequest[]
) => Promise<ApiResult<InternalProjectContact[]>> = async (
  projectId,
  projectMembers
) => {
  const result = await fetchWithRetry(
    `${apiUrl}/project/projectMember/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(projectMembers),
    }
  );
  const data = await result?.json();
  return {
    result,
    data,
  };
};

export const apiFetchProjectMemberships: (
  contactId: string,
  signal?: AbortSignal
) => Promise<ApiResult<ProjectMemberSearchResult>> = async (
  contactId,
  signal
) => {
  const searchString = `ContactId eq '${contactId}'`;
  const result = await fetchWithRetry(
    `${apiUrl}/search/Search?searchType=projectMembers&searchString=${searchString}&forceThreshold=10000&forceResult=true`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
      signal,
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

export const apiFetchContactsLastChanged: (
  offset: DateTimeString
) => Promise<ApiResult<ContactsCompaniesSearchResult>> = async (offset) => {
  const fromDate = moment(offset)
    .utc()
    .startOf('day')
    .toISOString()
    .split('Z')[0];
  const toDate = moment().utc().endOf('day').toISOString().split('Z')[0];

  const result = await fetchWithRetry(
    `${apiUrl}/search/Search?searchType=companyContacts&searchString=${encodeURIComponent(
      `CompanyContact eq 'Contact' & LastChanged ge '${fromDate}' & LastChanged le '${toDate}'`
    )}&forceThreshold=10000&forceResult=true`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

export const apiFetchContactsCreated: (
  offset: DateTimeString
) => Promise<ApiResult<ContactsCompaniesSearchResult>> = async (offset) => {
  const fromDate = moment(offset)
    .utc()
    .startOf('day')
    .toISOString()
    .split('Z')[0];
  const toDate = moment().utc().endOf('day').toISOString().split('Z')[0];

  const result = await fetchWithRetry(
    `${apiUrl}/search/Search?searchType=companyContacts&searchString=${encodeURIComponent(
      `CompanyContact eq 'Contact' & Created ge '${fromDate}' & Created le '${toDate}'`
    )}&forceThreshold=10000&forceResult=true`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};
